/**
 * New-copy/new-UI grouping/rollup layer — NOT shared taxonomy, NOT consumed
 * by `legacyCopy.ts` or the existing UI.
 *
 * Buckets the `Issue[]` produced by `collectors/index.ts`'s `collectAllIssues`
 * by `kind` + `groupKey` and computes the rollup fields the mockup's
 * collapsed rows need. No category-level bucketing yet — that needs
 * `newCopy.ts`'s `category` field, which doesn't exist until Phase 3.
 */
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IExperience } from '@dadohr/shared/domainTypes/experience';
import {
  getParticipantActor,
  getUserActorForKey
} from '../../../../../services/experiences/instance';
import { toMomentFromAny } from '../../../../../services/time';
import { AUTOMATION_USER_ID_KINDS, Issue, IssueKind, NO_ACTOR_AUTOMATION_KINDS } from './types';

export type GroupedIssue = {
  kind: IssueKind;
  groupKey: string;
  occurrences: Issue[];
  count: number;
  affectedParticipantIds: string[];
  /** Millis, or null if no occurrence in the group has a resolvable date. */
  lastAttemptAt: number | null;
};

/** Mirrors `getRowDate` in ../InstanceProblems.tsx (kept private there,
 * operates on `ExperienceProblemData`) but works off the normalized
 * `IssueRaw` shape instead, since a `GroupedIssue`'s occurrences only carry
 * that far. Optional-chained throughout and wrapped in try/catch: one
 * occurrence with an unexpected/partial shape (messy older Firestore data —
 * see the same caution in `getRowDateMillis`'s own comment) should drop that
 * occurrence's timestamp, not crash grouping for every other occurrence.
 * Exported (not just used for the group-level `lastAttemptAt` max) since
 * Phase 3's expanded per-occurrence sub-table needs a "last attempt" value
 * per row, not just per group. */
export function getOccurrenceTimestamp(issue: Issue): number | null {
  try {
    const raw = issue.raw;
    // Loosely typed, same as `getRowDate` in ../InstanceProblems.tsx: this
    // can be a Firestore Timestamp, a plain {seconds}/{_seconds} map, or a
    // raw epoch-ms number depending on which family/vintage wrote it.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const at: any =
      raw.family === 'AUTOMATION'
        ? (raw.action as { doneAt?: unknown })?.doneAt
        : raw.family === 'SENT_ITEM'
        ? raw.data.message?.at
        : raw.family === 'MISSING_DATA'
        ? raw.data.instance?.data?.startsAt
        : (raw.data as { at?: unknown })?.at;
    if (!at) return null;
    const millis = toMomentFromAny(at).valueOf();
    return Number.isFinite(millis) ? millis : null;
  } catch {
    return null;
  }
}

function getInstanceFromRaw(raw: Issue['raw']): Doc<IExperience> | undefined {
  return raw.family === 'AUTOMATION' ? raw.instance : raw.data.instance;
}

/** Not the same as `getAffectedUserId` which can be other actors in this instance. */
export function getOccurrenceParticipantUserId(issue: Issue): string | undefined {
  const instance = getInstanceFromRaw(issue.raw);
  return instance ? getParticipantActor(instance)?.userId : undefined;
}

function resolveActorKeyUserId(
  instance: Doc<IExperience> | undefined,
  actorKey: string | undefined
): string | undefined {
  if (!actorKey || !instance) return undefined;
  return getUserActorForKey(instance, actorKey)?.userId || undefined;
}

// Kinds whose raw error identifies a specific actor other than "the enrolled
// participant" — the supporting-actor/signer family and CC's role-targeted
// kinds. All of these are stored errors that stay open until an explicit
// Retry/Ignore, so the role can legitimately be filled after the error was
// recorded — resolving to whoever's currently in it is a real answer for a
// still-open issue, not a stale one. Some can still fail to resolve to any
// real user (e.g. ACTOR_NOT_FOUND, where the referenced role doesn't match
// anything on the current template at all).
const ACTOR_SPECIFIC_SUPPORTING_KINDS = new Set<IssueKind>([
  IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__INVALID_EMAIL,
  IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__USER_NOT_FOUND,
  IssueKind.CANNOT_ASSIGN_SUPPORTING_ACTOR__ACTOR_NOT_FOUND,
  IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_EMAIL,
  IssueKind.CANNOT_SEND_DOCUSIGN_ENVELOPE__NO_ACTOR
]);

const ACTOR_SPECIFIC_CC_KINDS = new Set<IssueKind>([
  IssueKind.CC__NO_ACTOR,
  IssueKind.CC__NO_EMAIL,
  IssueKind.CC__ROLE_EMAIL_BOUNCED
]);

/**
 * The user that the issue is about; not always the participant. For
 * instance, failure to send a CC to the manager. Returns `undefined` when
 * the specific actor doesn't resolve.
 */
function getAffectedUserId(issue: Issue): string | undefined {
  const { kind, raw } = issue;

  if (raw.family === 'ASSIGNEE_BOUNCE') {
    // No actor key on this raw shape at all — resolving "which actor" needs
    // new email-to-user matching logic that doesn't exist yet. Report
    // nobody rather than guess.
    return undefined;
  }

  if (raw.family === 'AUTOMATION' && (NO_ACTOR_AUTOMATION_KINDS.has(kind) || AUTOMATION_USER_ID_KINDS.has(kind))) {
    const actorKey = (raw.action as any).errorData?.data?.actorKey;
    return resolveActorKeyUserId(raw.instance, actorKey);
  }

  if (raw.family === 'SENT_ITEM') {
    return resolveActorKeyUserId(raw.data.instance, raw.data.actor);
  }

  if (raw.family === 'INSTANCE_SUBTASK' && ACTOR_SPECIFIC_SUPPORTING_KINDS.has(kind)) {
    const data = raw.data as { instance: Doc<IExperience>; actorKey?: string };
    return resolveActorKeyUserId(data.instance, data.actorKey);
  }

  if (kind === IssueKind.MISSING_ACTOR) {
    // Unlike the stored-error kinds above (which stay open until an explicit
    // Retry/Ignore, independent of whether the role gets filled in the
    // meantime), this kind is recomputed live on every collection pass by
    // `collectMissingSupportingActors` — it only exists in the list because
    // the exact same actor-key lookup this function would run already found
    // nobody assigned, on this same instance snapshot. If the role were
    // filled, this issue wouldn't be produced at all on the next pass, so
    // there's no live state in which resolving the actor key here could
    // return a different answer. Short-circuiting makes that explicit
    // instead of relying on it holding implicitly.
    return undefined;
  }

  if (raw.family === 'CC' && ACTOR_SPECIFIC_CC_KINDS.has(kind)) {
    return resolveActorKeyUserId(raw.data.instance, raw.data.recipientId);
  }

  // Default bucket: falls back to the participant. Unlike the branches above,
  // nothing here is Set-driven — a kind lands here simply by not matching
  // anything earlier, so this is the safe default for any kind not
  // deliberately categorized as actor-specific. Examples below are
  // illustrative, not an exhaustive or maintained list: CC__FIXED_EMAIL_BOUNCED
  // (a static configured address, not a role), ENROLL_IN_EXPERIENCE__*,
  // background check, Salesforce, MISSING_VAR/MISSING_DATA__UNKNOWN,
  // HTTP_POST__*/SYNC_TO_DATA_DESTINATION/UNENROLL_FROM_EXPERIENCE/
  // CREATE_DOCUMENT_FROM_TEMPLATE__*, INSTANCE_SUBTASK__UNKNOWN, UNKNOWN, and
  // every automation kind with no actor concept at all (API_ERROR/DEFAULT-ish
  // connectivity failures).
  const instance = getInstanceFromRaw(raw);
  return instance ? getParticipantActor(instance)?.userId : undefined;
}

export function groupIssues(issues: Issue[]): GroupedIssue[] {
  const groups = new Map<string, GroupedIssue>();

  for (const issue of issues) {
    const bucketKey = `${issue.kind}::${issue.groupKey}`;
    const existing = groups.get(bucketKey);
    if (existing) {
      existing.occurrences.push(issue);
    } else {
      groups.set(bucketKey, {
        kind: issue.kind,
        groupKey: issue.groupKey,
        occurrences: [issue],
        count: 0, // filled in below, once every occurrence has landed
        affectedParticipantIds: [],
        lastAttemptAt: null
      });
    }
  }

  return [...groups.values()].map((group) => {
    const participantIds = new Set<string>();
    let lastAttemptAt: number | null = null;
    for (const occurrence of group.occurrences) {
      const affectedUserId = getAffectedUserId(occurrence);
      if (affectedUserId) participantIds.add(affectedUserId);
      const at = getOccurrenceTimestamp(occurrence);
      if (at !== null && (lastAttemptAt === null || at > lastAttemptAt)) lastAttemptAt = at;
    }
    return {
      ...group,
      count: group.occurrences.length,
      affectedParticipantIds: [...participantIds],
      lastAttemptAt
    };
  });
}
